// The session, and the ways in: an 8-digit code the hub emails itself (decision 41), the login link
// in the same email (the same login's token, used on /sign-in when Log in is pressed: lib/loginLink.js
// and screens/SignIn.jsx), a password, and nothing else. The code request goes to the hub's own
// request-code function, which answers every email the same way; Supabase Auth never sends the hub
// an email.
import { useEffect, useState } from 'react'
import { supabase } from './supabase.js'
import { ENV } from './env.js'
import { clearSavedWorking } from '../product/toolRunner.js'
import { STRATEGY_RUN_KEY } from '../strategy/route.js'

// The online tools' working kept in this browser (src/product/ToolPage.jsx) goes when a person logs
// out, by any way out: Log out in Account or on the no-account page, a session ended from somewhere
// else (Log out on every device, or staff's Log out everywhere, seen here as the login runs out), and
// Log out on every device pressed here, which keeps this session but clears this person's working on
// this device. With no person named, every tool's working in this browser goes: only 1 person is
// logged in here at a time, so anything else is left over. Storage that's off or full changes nothing.
// The property strategy tool's run in this tab (sessionStorage, strategy/app.js) goes too, every time, so a run kept in
// a tab is never saved to the next person who logs in there.
export function forgetToolWorking(personId = null) {
  try {
    window.sessionStorage.removeItem(STRATEGY_RUN_KEY)
  } catch { /* storage off */ }
  try {
    return clearSavedWorking(window.localStorage, personId)
  } catch {
    return 0
  }
}

// { status: 'loading' | 'out' | 'in', session }
export function useAuth() {
  const [state, setState] = useState({ status: 'loading', session: null })
  useEffect(() => {
    let alive = true
    supabase.auth.getSession().then(({ data }) => {
      if (alive) setState(s => (s.status === 'loading' ? { status: data.session ? 'in' : 'out', session: data.session } : s))
    })
    // no Supabase call inside this callback: supabase-js holds a lock while it runs
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // logged out here, in another tab, or by a session ended elsewhere: the tools' working goes too
      if (event === 'SIGNED_OUT') forgetToolWorking()
      if (alive) setState({ status: session ? 'in' : 'out', session })
    })
    return () => { alive = false; data.subscription.unsubscribe() }
  }, [])
  return state
}

export const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// Asks the hub to email a code. The answer is the same for every email, so there's nothing to read
// from it: a network failure is the only thing worth telling the member about. A buyer back from a
// payment link sends the checkout's session id too (MS-03), so the hub can confirm the payment with
// Stripe first if Stripe's own event hasn't reached it yet. A product's door sends its key too, so the
// email's login link opens in that door's look; from the Library's door, the body is the email alone.
export async function requestCode(email, sessionId = null, door = null) {
  const body = { email }
  if (sessionId) body.session_id = sessionId
  if (door && door !== 'library') body.door = door
  const res = await fetch(`${ENV.supabaseUrl}/functions/v1/request-code`, {
    method: 'POST',
    // the publishable key goes on apikey only: it isn't a JWT, so it never goes in Authorization
    headers: { 'content-type': 'application/json', apikey: ENV.publishableKey },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`request-code answered ${res.status}`)
}

export function verifyCode(email, code) {
  return supabase.auth.verifyOtp({ email, token: code, type: 'email' })
}

// The login link's token, only once Log in is pressed. It goes in the body of Supabase's verify
// call, never in an address. Type email, as for the code: the token is the same login's.
export function verifyLink(tokenHash) {
  return supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'email' })
}

export function passwordLogIn(email, password) {
  return supabase.auth.signInWithPassword({ email, password })
}

// This browser only. supabase-js signs out everywhere by default, which on live would also end the
// person's login to course.adamslee.co.uk on every device, since it shares the Supabase user. Log out everywhere is its own function (data-model section 10). The tools'
// working goes first, so it's gone even if the sign-out call fails.
export function logOut() {
  forgetToolWorking()
  return supabase.auth.signOut({ scope: 'local' })
}

// phone, tablet or laptop, for AdminPerson's "Last in today at 08:12, on a phone"
export function deviceKind() {
  const ua = navigator.userAgent || ''
  if (/iPad|Tablet/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'tablet'
  if (/Mobi|iPhone|Android/i.test(ua)) return 'phone'
  return 'laptop'
}

// A login_events row and people.last_login_at. Best effort: a login with no people row has nothing
// to record, and the member shouldn't wait on it.
export function recordLogin(method) {
  supabase.rpc('record_login', { p_method: method, p_device: deviceKind() }).then(() => {}, () => {})
}
